import { Box, Grid, Typography } from "@mui/material";
import { MainContext } from "../../../../main/context/MainContextProvider";
import { useContext } from "react";
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { RopCard1110RootCauseAnalysisTaticoAggregated } from "../../types";
import AnaliseCausaRaizCascataChart from "./AnaliseCausaRaizCascataChart";
import AnaliseCausaRaizDecomposicaoTable, { LossCategoryData } from "./AnaliseCausaRaizDecomposicaoTable";
import AnaliseCausaRaizChart from "./AnaliseCausaRaizChart";
import { LossType } from "./LossCategoryCard";
/* import PerdasEvitaveisAcoesInternas from "./PerdasEvitaveisAcoesInternas";
import PerdasInevitaveisAcoesExternas from "./PerdasInevitaveisAcoesExternas"; */
import InsightPrioritario from "./InsightPrioritario";
import LossTotalizerCards from "./LossTotalizerCards";
import TopLossesCard, { LossItem } from "./TopLossesCard";
import PVSystComparisonCard from "./PVSystComparisonCard";

export type AnaliseCausaRaizProps = {
    data: RopCard1110RootCauseAnalysisTaticoAggregated;
    viewMode?: 'powerPlant' | 'cluster';
};

export default function AnaliseCausaRaiz({ data, viewMode = 'cluster' }: AnaliseCausaRaizProps) {
    const { theme } = useContext(MainContext);

    const eventsCount = data.powerPlantTotals.length;

    // Função para converter duração em horas
    const parseDuration = (duration: string): number => {
        // Formato esperado: "HH:MM:SS" ou similar
        const parts = duration.split(':');
        if (parts.length === 3) {
            const hours = parseInt(parts[0]) || 0;
            const minutes = parseInt(parts[1]) || 0;
            const seconds = parseInt(parts[2]) || 0;
            return hours + (minutes / 60) + (seconds / 3600);
        }
        return 0;
    };

    // Calcular duração total em horas
    const stringFailuresDuration = parseDuration(data.stringFailures.overallData.duration);
    const inverterFailuresDuration = parseDuration(data.inverterFailures.overallData.duration);
    const trackerFailuresDuration = parseDuration(data.trackerFailures.overallData.duration);

    const totalDuration = stringFailuresDuration + inverterFailuresDuration + trackerFailuresDuration;

    // Calcular percentuais baseados na duração
    const stringFailuresPercent = totalDuration > 0 ? (data.overallTotal.stringUnavailabilityLoss / totalDuration) * 100 : 0;
    const inverterFailuresPercent = totalDuration > 0 ? (data.overallTotal.inverterUnavailabilityLoss / totalDuration) * 100 : 0;
    const otherFailuresPercent = totalDuration > 0 ? (data.overallTotal.otherOperationalLosses / totalDuration) * 100 : 0;

    // Calcular percentuais de perdas previstas baseado no total de perdas previstas
    const totalExpectedLosses = data.overallTotal.expectedLossesTotal;
    const opticalPercent = totalExpectedLosses > 0 ? (data.overallTotal.opticalAndIrradiationLosses / totalExpectedLosses) * 100 : 0;
    const conversionPercent = totalExpectedLosses > 0 ? (data.overallTotal.conversionAndDCModuleLosses / totalExpectedLosses) * 100 : 0;
    const inverterEffPercent = totalExpectedLosses > 0 ? (data.overallTotal.inverterEfficiencyLosses / totalExpectedLosses) * 100 : 0;
    const transformerPercent = totalExpectedLosses > 0 ? (data.overallTotal.transformerEfficiencyLosses / totalExpectedLosses) * 100 : 0;

    // Preparar dados das categorias de perda (Gerenciáveis)
    const evitaveisData: LossCategoryData[] = [
        {
            title: "Indisponibilidade do Inversor",
            type: LossType.EVITAVEL,
            percentage: inverterFailuresPercent,
            usinas: data.powerPlantTotals.filter(p => p.data.inverterUnavailabilityLoss > 0).length,
            totalValue: `${data.overallTotal.inverterUnavailabilityLoss.toFixed(2)} MWh`,
            valueSuffix: 'MWh',
            usinaDetails: data.powerPlantTotals
                .filter(p => p.data.inverterUnavailabilityLoss > 0)
                .map(p => ({
                    name: p.usinaName,
                    value: p.data.inverterUnavailabilityLoss,
                }))
        },
        {
            title: "Indisponibilidade da String",
            type: LossType.EVITAVEL,
            percentage: stringFailuresPercent,
            usinas: data.powerPlantTotals.filter(p => p.data.stringUnavailabilityLoss > 0).length,
            totalValue: `${data.overallTotal.stringUnavailabilityLoss.toFixed(2)} MWh`,
            valueSuffix: 'MWh',
            usinaDetails: data.powerPlantTotals
                .filter(p => p.data.stringUnavailabilityLoss > 0)
                .map(p => ({
                    name: p.usinaName,
                    value: p.data.stringUnavailabilityLoss,
                }))
        },
        {
            title: "Outras Operacionais",
            type: LossType.EVITAVEL,
            percentage: otherFailuresPercent,
            usinas: data.powerPlantTotals.filter(p => p.data.otherOperationalLosses > 0).length,
            totalValue: `${data.overallTotal.otherOperationalLosses.toFixed(2)} MWh`,
            valueSuffix: 'MWh',
            usinaDetails: data.powerPlantTotals
                .filter(p => p.data.otherOperationalLosses > 0)
                .map(p => ({
                    name: p.usinaName,
                    value: p.data.otherOperationalLosses,
                }))
        }
    ];

    // Preparar dados das categorias de perda (Previstas)
    const previstasData: LossCategoryData[] = [
        {
            title: "Ópticas & Irradiação",
            type: LossType.PREVISTA,
            percentage: opticalPercent,
            usinas: data.powerPlantTotals.filter(p => p.data.opticalAndIrradiationLosses > 0).length,
            totalValue: `${data.overallTotal.opticalAndIrradiationLosses.toFixed(2)} MWh`,
            valueSuffix: 'MWh',
            usinaDetails: data.powerPlantTotals
                .filter(p => p.data.opticalAndIrradiationLosses > 0)
                .map(p => ({
                    name: p.usinaName,
                    value: p.data.opticalAndIrradiationLosses,
                }))
        },
        {
            title: "Conversão CC / Módulos",
            type: LossType.PREVISTA,
            percentage: conversionPercent,
            usinas: data.powerPlantTotals.filter(p => p.data.conversionAndDCModuleLosses > 0).length,
            totalValue: `${data.overallTotal.conversionAndDCModuleLosses.toFixed(2)} MWh`,
            valueSuffix: 'MWh',
            usinaDetails: data.powerPlantTotals
                .filter(p => p.data.conversionAndDCModuleLosses > 0)
                .map(p => ({
                    name: p.usinaName,
                    value: p.data.conversionAndDCModuleLosses,
                }))
        },
        {
            title: "Eficiência do Inversor",
            type: LossType.PREVISTA,
            percentage: inverterEffPercent,
            usinas: data.powerPlantTotals.filter(p => p.data.inverterEfficiencyLosses > 0).length,
            totalValue: `${data.overallTotal.inverterEfficiencyLosses.toFixed(2)} MWh`,
            valueSuffix: 'MWh',
            usinaDetails: data.powerPlantTotals
                .filter(p => p.data.inverterEfficiencyLosses > 0)
                .map(p => ({
                    name: p.usinaName,
                    value: p.data.inverterEfficiencyLosses,
                }))
        },
        {
            title: "Eficiência do Transformador",
            type: LossType.PREVISTA,
            percentage: transformerPercent,
            usinas: data.powerPlantTotals.filter(p => p.data.transformerEfficiencyLosses > 0).length,
            totalValue: `${data.overallTotal.transformerEfficiencyLosses.toFixed(2)} MWh`,
            valueSuffix: 'MWh',
            usinaDetails: data.powerPlantTotals
                .filter(p => p.data.transformerEfficiencyLosses > 0)
                .map(p => ({
                    name: p.usinaName,
                    value: p.data.transformerEfficiencyLosses,
                }))
        }
    ];

    // Combinar todas as perdas
    const lossData: LossCategoryData[] = [...evitaveisData, ...previstasData];

    const getModeName = () => {
        if (viewMode === 'cluster') {
            return 'Cluster';
        }
        return 'Usina';
    };

    // Preparar dados para Top 3 Perdas Gerenciáveis (baseado em valores reais)
    const totalRecoverable = data.overallTotal.recoverableLossesTotal;
    const avoidableLosses: LossItem[] = [
        {
            name: "Indisponibilidade do Inversor",
            message: "Priorizar manutenção preventiva e monitoramento de inversores.",
            value: data.overallTotal.inverterUnavailabilityLoss,
            percentage: totalRecoverable > 0 ? (data.overallTotal.inverterUnavailabilityLoss / totalRecoverable) * 100 : 0
        },
        {
            name: "Indisponibilidade da String",
            message: "Realizar inspeção térmica e elétrica das strings com maior histórico de falhas.",
            value: data.overallTotal.stringUnavailabilityLoss,
            percentage: totalRecoverable > 0 ? (data.overallTotal.stringUnavailabilityLoss / totalRecoverable) * 100 : 0
        },
        {
            name: "Outras Operacionais",
            message: "Revisar processos operacionais e identificar padrões de curtailment ou paradas não planejadas.",
            value: data.overallTotal.otherOperationalLosses,
            percentage: totalRecoverable > 0 ? (data.overallTotal.otherOperationalLosses / totalRecoverable) * 100 : 0
        }
    ].sort((a, b) => b.value - a.value).slice(0, 3);

    // Preparar dados para Top 3 Perdas Previstas (baseado em valores reais)
    const totalExpected = data.overallTotal.expectedLossesTotal;
    const expectedLosses: LossItem[] = [
        {
            name: "Ópticas & Irradiação",
            message: "Perdas por sujeira e degradação são inerentes. Considerar aumento de frequência de limpeza dos módulos.",
            value: data.overallTotal.opticalAndIrradiationLosses,
            percentage: totalExpected > 0 ? (data.overallTotal.opticalAndIrradiationLosses / totalExpected) * 100 : 0
        },
        {
            name: "Conversão CC / Módulos",
            message: "Perdas de conversão são inerentes à tecnologia fotovoltaica. Considerar upgrade de módulos em próxima expansão.",
            value: data.overallTotal.conversionAndDCModuleLosses,
            percentage: totalExpected > 0 ? (data.overallTotal.conversionAndDCModuleLosses / totalExpected) * 100 : 0
        },
        {
            name: "Eficiência do Inversor",
            message: "Perdas de eficiência do inversor são normais. Verificar se eficiência está dentro do esperado pelo fabricante.",
            value: data.overallTotal.inverterEfficiencyLosses,
            percentage: totalExpected > 0 ? (data.overallTotal.inverterEfficiencyLosses / totalExpected) * 100 : 0
        },
        {
            name: "Eficiência do Transformador",
            message: "Perdas do transformador são inerentes. Verificar se estão dentro da especificação técnica.",
            value: data.overallTotal.transformerEfficiencyLosses,
            percentage: totalExpected > 0 ? (data.overallTotal.transformerEfficiencyLosses / totalExpected) * 100 : 0
        }
    ].sort((a, b) => b.value - a.value).slice(0, 3);

    return (
        <Grid
            item
            xs={12}
            marginY={"12px"}
            sx={{
                height: "100%",
                padding: "16px",
                paddingBottom: 0,
                marginLeft: "12px",
                backgroundColor: theme.cardBoxBackgroudColor,
                borderRadius: "8px",
                display: "flex",
                flexDirection: "column"
            }}
        >
            {/* Header */}
            <Box sx={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "16px" }}>
                <Box
                    sx={{
                        backgroundColor: "rgba(251, 146, 60, 0.15)",
                        borderRadius: "8px",
                        width: "40px",
                        height: "40px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center"
                    }}
                >
                    <TrendingUpIcon sx={{ color: "#FB923C", fontSize: 22 }} />
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                    <Typography sx={{ fontSize: "18px", fontWeight: "500" }}>
                        Análise de Causa Raiz
                    </Typography>
                    <Typography sx={{ fontSize: "13px", fontWeight: "300", color: theme.secondaryTextColor }}>
                        Perdas por categoria - {eventsCount > 0 ? `${getModeName()}` : 'Semana'}
                    </Typography>
                </Box>
            </Box>

            {/* Loss Totalizer Cards */}
            <LossTotalizerCards
                totalLoss={data.overallTotal.expectedLossesTotal + data.overallTotal.recoverableLossesTotal}
                expectedLosses={data.overallTotal.expectedLossesTotal}
                recoverableLosses={data.overallTotal.recoverableLossesTotal}
            />

            {/* Charts Section */}
            <Grid container spacing={1.5} sx={{ marginBottom: "16px" }}>
                <Grid item xs={12} md={6}>
                    <AnaliseCausaRaizChart
                        data={data.overallTotal}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <AnaliseCausaRaizCascataChart
                        data={data.overallTotal}
                    />
                </Grid>
                <Grid item xs={12} >
                    <AnaliseCausaRaizDecomposicaoTable lossData={lossData} />
                </Grid>
                {data.overallTotal.pvsystReference && (
                    <Grid item xs={12}>
                        <PVSystComparisonCard
                            pvsystReference={data.overallTotal.pvsystReference}
                            realOptical={data.overallTotal.opticalAndIrradiationLosses}
                            realConversion={data.overallTotal.conversionAndDCModuleLosses}
                            realInverter={data.overallTotal.inverterEfficiencyLosses}
                            realTransformer={data.overallTotal.transformerEfficiencyLosses}
                            idealEnergy={data.overallTotal.idealEnergy}
                        />
                    </Grid>
                )}
                {data.priorityInsight && (
                    <Grid item xs={12}>
                        <InsightPrioritario insight={data.priorityInsight} />
                    </Grid>
                )}
                <Grid item xs={12} md={6}>
                    <TopLossesCard
                        title="Top 3 Perdas Gerenciáveis"
                        lossType="avoidable"
                        losses={avoidableLosses}
                        totalLoss={data.overallTotal.recoverableLossesTotal}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <TopLossesCard
                        title="Top 3 Perdas Previstas"
                        lossType="expected"
                        losses={expectedLosses}
                        totalLoss={data.overallTotal.expectedLossesTotal}
                    />
                </Grid>
                {/*                 <Grid item xs={12} md={6}>
                    <PerdasEvitaveisAcoesInternas />
                </Grid>
                <Grid item xs={12} md={6}>
                    <PerdasInevitaveisAcoesExternas />
                </Grid> */}
            </Grid>
        </Grid>
    );
}
